import { useRef, useState } from "react";
import { useConditionsContext } from "./ConditionsDialog";
import { X, AlertCircle } from "lucide-react";
import { processFileForUpload } from "src/utils/conditionFileValidation";
import type { NewConditionInput } from "src/types/conditions";

interface NewConditionUploadProps {
  stepNumber: number;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

/**
 * Component for adding new conditions with attached files.
 * Only displayed when Porchpass is the selected lender.
 */
export default function NewConditionUpload({
  stepNumber,
}: NewConditionUploadProps) {
  const { newConditions, addNewCondition, removeNewCondition } =
    useConditionsContext();

  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState("");
  const [tag, setTag] = useState<string>("customer");
  const status = "not_done";
  const [internalDescription, setInternalDescription] = useState("");
  const [externalDescription, setExternalDescription] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleFileSelect = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setErrors([]);
    setIsProcessing(true);
    const file = files[0]; // Only allow one file per condition

    try {
      // Validate and convert file to PDF if necessary
      const processedFile = await processFileForUpload(file);
      setSelectedFile(processedFile);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to process file";
      setErrors([errorMessage]);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    void handleFileSelect(e.target.files);
    // Reset input value so the same file can be selected again
    if (inputRef.current) {
      inputRef.current.value = "";
    }
  };

  const handleAddCondition = (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    // Validate required fields
    if (!title.trim()) {
      setErrors(["Condition title is required"]);
      return;
    }

    if (!selectedFile) {
      setErrors(["A file must be selected"]);
      return;
    }

    // Add new condition
    const newCondition: NewConditionInput = {
      title: title.trim(),
      tag,
      status,
      file: selectedFile,
      internalDescription: internalDescription.trim() || undefined,
      externalDescription: externalDescription.trim() || undefined,
    };

    addNewCondition(newCondition);

    // Reset form
    setTitle("");
    setTag("customer");
    setInternalDescription("");
    setExternalDescription("");
    setSelectedFile(null);
  };

  return (
    <div>
      <div className="flex flex-row gap-2 text-[14px] font-semibold mb-4 mt-6 pt-4 border-t border-gray-200">
        <span className="text-[#1453F5] bg-[#E1E8FF] rounded-full w-6 h-6 flex items-center justify-center">
          {stepNumber}
        </span>
        <p className="text-[#697083] flex items-center justify-center">
          ADD NEW CONDITIONS (OPTIONAL)
        </p>
      </div>
      <p className="text-sm font-normal text-[#4A5466] mb-4">
        Create new conditions without pre-existing requirements from DigiFi.
      </p>

      {/* New Condition Form */}
      <form onSubmit={handleAddCondition} className="space-y-4 mb-4">
        {/* Title Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Condition Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., Insurance Certificate, Tax Return"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            disabled={isProcessing}
          />
        </div>

        {/* Tag */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category <span className="text-red-500">*</span>
          </label>
          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            disabled={isProcessing}
          >
            <option value="customer">Customer</option>
            <option value="funding">Funding</option>
            <option value="seller">Seller</option>
          </select>
        </div>

        {/* Descriptions */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Internal Description
          </label>
          <textarea
            value={internalDescription}
            onChange={(e) => setInternalDescription(e.target.value)}
            placeholder="Description for internal use only (not shown to borrower)"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={2}
            disabled={isProcessing}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            External Description
          </label>
          <textarea
            value={externalDescription}
            onChange={(e) => setExternalDescription(e.target.value)}
            placeholder="Description shown to borrower"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 resize-none"
            rows={2}
            disabled={isProcessing}
          />
        </div>

        {/* File Upload */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attach Document <span className="text-red-500">*</span>
          </label>

          {selectedFile ? (
            // Show file preview when file is selected
            <div className="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-900">
              <span
                className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap"
                title={selectedFile.name}
              >
                {selectedFile.name}
              </span>
              <span className="ml-2 text-gray-500 text-xs flex-shrink-0">
                {formatFileSize(selectedFile.size)}
              </span>
              <button
                type="button"
                onClick={() => setSelectedFile(null)}
                aria-label={`Remove ${selectedFile.name}`}
                className="bg-none border-none cursor-pointer p-1 flex items-center justify-center text-gray-500 transition-colors ease-200 ml-2 flex-shrink-0 hover:text-red-600"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            // Show upload box only when no file is selected
            <div
              className="border-2 border-dashed rounded-lg px-4 py-6 text-center cursor-pointer transition-all ease-200 border-gray-300 bg-transparent hover:border-blue-600 hover:bg-gray-50"
              onClick={() => !isProcessing && inputRef.current?.click()}
            >
              <p className="text-sm text-gray-500 m-0">
                <strong className="text-gray-900">Click to upload</strong> or
                drag and drop
                <br />
                PDF, JPG, PNG
              </p>
            </div>
          )}

          <input
            ref={inputRef}
            type="file"
            onChange={handleInputChange}
            accept=".pdf,.png,.jpg,.jpeg"
            className="hidden"
            disabled={isProcessing}
          />
        </div>

        {/* Error Messages */}
        {errors.length > 0 && (
          <div className="space-y-2">
            {errors.map((error, index) => (
              <div
                key={index}
                className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800"
              >
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            ))}
          </div>
        )}

        {/* Add Button */}
        <button
          type="submit"
          disabled={isProcessing}
          className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
        >
          {isProcessing ? "Processing..." : "Add Condition"}
        </button>
      </form>

      {/* New Conditions List */}
      {newConditions.length > 0 && (
        <div className="space-y-2 mt-6 pt-4 border-t border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900">
            New Conditions ({newConditions.length})
          </h3>
          {newConditions.map((condition) => (
            <div
              key={condition.title}
              className="flex items-start justify-between p-3 bg-blue-50 border border-blue-200 rounded text-sm text-gray-900"
            >
              <div className="flex-1">
                <p className="font-medium text-gray-900">{condition.title}</p>
                <p className="text-xs text-gray-600">
                  {condition.file.name} •{" "}
                  <span className="capitalize">{condition.tag}</span> •{" "}
                  <span className="capitalize">{condition.status}</span>
                </p>
              </div>
              <button
                onClick={() => removeNewCondition(condition.title)}
                aria-label={`Remove ${condition.title}`}
                className="bg-none border-none cursor-pointer p-1 flex items-center justify-center text-gray-500 transition-colors ease-200 ml-2 flex-shrink-0 hover:text-red-600"
              >
                <X size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
